import { useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, EyeIcon, EyeOffIcon, LockKeyholeIcon } from "lucide-react";
import { useId, useState, type SyntheticEvent } from "react";

import { LoginRequestSchema, type WorkspaceEntryBrandingResponse } from "../../shared/schemas";
import { ApiClientError, navigateToWorkspace } from "../api/client";
import { useLoginMutation } from "../api/queries";
import { Brand } from "./Brand";
import { OfflineState } from "./OfflineState";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Field, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";

type WorkspaceBrand = NonNullable<WorkspaceEntryBrandingResponse["workspace"]>;

function useLoginController(isOnline: boolean, requestedOrganizationId: string | undefined) {
  const navigate = useNavigate({ from: "/login" });
  const mutation = useLoginMutation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setLocalError(null);
    if (!isOnline) return;
    const parsed = LoginRequestSchema.safeParse({ username, password });
    if (!parsed.success) {
      setLocalError("Enter a valid username and a password of at least 8 characters.");
      return;
    }
    mutation.mutate(parsed.data, {
      onSuccess: (me) => {
        const organization =
          me.organizations.find(({ id }) => id === requestedOrganizationId) ?? me.organizations[0];
        if (
          organization &&
          (window.location.hostname === "app.ccbox.ai" ||
            organization.id !== requestedOrganizationId)
        ) {
          navigateToWorkspace(organization.id, `/?org=${encodeURIComponent(organization.id)}`);
          return;
        }
        void navigate({
          to: "/",
          search: organization ? { org: organization.id } : {},
          replace: true,
        });
      },
    });
  }

  const remoteError =
    mutation.error instanceof ApiClientError
      ? mutation.error.message
      : mutation.isError
        ? "CCBox could not sign you in. Check the credentials and try again."
        : null;
  return {
    error: localError ?? remoteError,
    mutation,
    password,
    setPassword,
    setShowPassword,
    setUsername,
    showPassword,
    submit,
    username,
  };
}

type LoginController = ReturnType<typeof useLoginController>;

function CredentialFields({ controller }: { controller: LoginController }) {
  const usernameId = useId();
  const passwordId = useId();
  return (
    <FieldGroup className="gap-4">
      <Field data-invalid={Boolean(controller.error)}>
        <FieldLabel
          className="font-sans text-[12px] tracking-normal normal-case"
          htmlFor={usernameId}
        >
          Username
        </FieldLabel>
        <Input
          aria-invalid={Boolean(controller.error)}
          autoComplete="username"
          id={usernameId}
          inputMode="text"
          name="username"
          placeholder="e.g. dana.r"
          required
          value={controller.username}
          onChange={(event) => {
            controller.setUsername(event.currentTarget.value);
          }}
        />
      </Field>
      <Field data-invalid={Boolean(controller.error)}>
        <FieldLabel
          className="font-sans text-[12px] tracking-normal normal-case"
          htmlFor={passwordId}
        >
          Password
        </FieldLabel>
        <div className="relative">
          <Input
            aria-invalid={Boolean(controller.error)}
            autoComplete="current-password"
            className="pr-12"
            id={passwordId}
            name="password"
            placeholder="Enter your password"
            required
            type={controller.showPassword ? "text" : "password"}
            value={controller.password}
            onChange={(event) => {
              controller.setPassword(event.currentTarget.value);
            }}
          />
          <Button
            aria-label={controller.showPassword ? "Hide password" : "Show password"}
            aria-pressed={controller.showPassword}
            className="absolute top-0 right-0"
            onClick={() => {
              controller.setShowPassword((visible) => !visible);
            }}
            size="icon"
            type="button"
            variant="ghost"
          >
            {controller.showPassword ? (
              <EyeOffIcon aria-hidden="true" />
            ) : (
              <EyeIcon aria-hidden="true" />
            )}
          </Button>
        </div>
      </Field>
    </FieldGroup>
  );
}

function LoginForm({ controller, isOnline }: { controller: LoginController; isOnline: boolean }) {
  return (
    <form className="grid gap-5" noValidate onSubmit={controller.submit}>
      {!isOnline ? (
        <OfflineState
          description="CCBox needs an internet connection to sign in. Reconnect to Wi-Fi or cellular data; you can finish entering your credentials while you wait."
          variant="banner"
        />
      ) : null}
      <CredentialFields controller={controller} />
      {controller.error ? (
        <Alert variant="destructive">
          <LockKeyholeIcon aria-hidden="true" />
          <AlertTitle>Sign-in blocked</AlertTitle>
          <AlertDescription>{controller.error}</AlertDescription>
        </Alert>
      ) : null}
      <Button
        className="w-full"
        disabled={!isOnline || controller.mutation.isPending}
        size="lg"
        type="submit"
      >
        {!isOnline
          ? "Connect to sign in"
          : controller.mutation.isPending
            ? "Signing in…"
            : "Sign in"}
        <ArrowRightIcon aria-hidden="true" data-icon="inline-end" />
      </Button>
      <p className="m-0 text-center text-[12.5px] leading-relaxed text-muted-foreground">
        Trouble signing in? Ask your office manager to reset your access.
      </p>
    </form>
  );
}

export function LoginCard({
  isOnline,
  requestedOrganizationId,
  workspace,
}: {
  isOnline: boolean;
  requestedOrganizationId: string | undefined;
  workspace: WorkspaceBrand | null;
}) {
  const controller = useLoginController(isOnline, requestedOrganizationId);
  const workspaceBrand = workspace?.logoVersion
    ? { name: workspace.name, logoSrc: `/api/workspace-entry/logo?v=${workspace.logoVersion}` }
    : undefined;
  return (
    <Card className="workspace-login-card w-full">
      <CardHeader className="items-center gap-3 text-center">
        <Brand
          className="workspace-login-brand mb-1 justify-self-center"
          {...(workspaceBrand ? { workspace: workspaceBrand } : {})}
        />
        <div>
          <CardTitle className="text-balance font-heading text-[21px]" id="login-title">
            Sign in to {workspace?.name ?? "CCBox"}
          </CardTitle>
          <CardDescription className="mt-1.5 text-pretty text-[13px]">
            {workspace ? "Your team's tools, all in one place." : "Your tools, all in one place."}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <LoginForm controller={controller} isOnline={isOnline} />
      </CardContent>
    </Card>
  );
}
